import type { ChatAttachment, ChatReplyPreview } from "@/api/dashboard";

const STORAGE_KEY = "kb:chat:pending-send:v1";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface PendingSendRecovery {
  id: string;
  userId: string;
  topicId: number;
  text: string;
  replyTo: ChatReplyPreview | null;
  attachments: ChatAttachment[];
  createdAt: number;
}

export function createPendingSendRecovery(
  value: Omit<PendingSendRecovery, "id" | "createdAt">,
): PendingSendRecovery {
  const random = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return { ...value, id: random, createdAt: Date.now() };
}

export function storePendingSend(value: PendingSendRecovery): void {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch { /* quota / policy */ }
}

export function readPendingSend(userId: string, topicId: number): PendingSendRecovery | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<PendingSendRecovery>;
    const valid = typeof value.id === "string"
      && value.userId === userId
      && value.topicId === topicId
      && typeof value.text === "string"
      && Array.isArray(value.attachments)
      && typeof value.createdAt === "number";
    if (!valid || Date.now() - value.createdAt! > MAX_AGE_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return value as PendingSendRecovery;
  } catch {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    return null;
  }
}

export function clearPendingSend(id: string): void {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const value = JSON.parse(raw) as Partial<PendingSendRecovery>;
    if (value.id === id) sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }
}

